import { BenefitCode, Medication, OrderView } from '../../__generated__/graphql';
import currencyjs from 'currency.js';
import currency from 'currency.js';
import _ from 'underscore';

export type DiscountCalculationItem = {
  medication: Medication;
  qty: number;
};

export const amountDescriptionForCode = (code?: BenefitCode | null | undefined) => {
  if (code?.discountType === 'amount')
    return `${currencyjs(code?.discount ?? 0).format()} off ${code?.type}`;

  return `${code?.discount}% off ${code?.type}`;
};

export const orderViewToDiscountItem = (item: OrderView) => {
  return {
    medication: item?.medication,
    qty: item?.prescription?.qty ?? 1,
  } as DiscountCalculationItem;
};

const calculateEffectiveAmount = (code: BenefitCode, price: number) => {
  const priceObj = currency(price);

  if (code?.discountType === 'amount') {
    return code?.discount ?? 0;
  }

  const percentage = priceObj.multiply((code?.discount ?? 0) / 100);

  return percentage.value;
};

export const calculateServiceDiscountFromCodes = (
  appliedCodes: BenefitCode[],
  serviceID?: string | null | undefined,
  servicePrice?: number | null | undefined,
) => {
  if (!serviceID || !servicePrice) return { total: currencyjs(0), usedCodes: [] };

  const applicableDiscountCodes =
    appliedCodes?.filter(
      (a) =>
        (a.type === 'services' || a.type === 'customerRefer') &&
        a?.active &&
        (a?.services?.length === 0 || a?.services?.includes(serviceID)),
    ) ?? [];

  const summedDiscounts = applicableDiscountCodes
    ?.map((code) => calculateEffectiveAmount(code, servicePrice))
    ?.reduce((acc, current) => acc + current, 0);

  const total = currency(summedDiscounts);

  return {
    total,
    usedCodes: applicableDiscountCodes,
  };
};

export const calculateProductDiscountFromCodes = (
  appliedCodes: BenefitCode[],
  items: DiscountCalculationItem[],
  shippingMethod: string | null | undefined,
  shippingMethodAmount: number | null | undefined,
  cartTotal: number,
) => {
  const applicableProductCodes = appliedCodes?.filter((a) => a.type === 'products' && a?.active);

  let usedCodes: BenefitCode[] = [];

  const productDiscounts = items?.map((item) => {
    const applicableProductCodesForItem = applicableProductCodes?.filter(
      (a) =>
        (a.services?.length === 0 || a?.services?.includes(item?.medication?.id ?? '')) &&
        cartTotal >= (a?.spendQualify ?? 0),
    );

    if (applicableProductCodesForItem) {
      usedCodes = [...usedCodes, ...applicableProductCodesForItem];

      const summedDiscounts = applicableProductCodesForItem
        ?.map((code) =>
          calculateEffectiveAmount(
            code,
            currencyjs(item?.medication?.retail ?? 0).multiply(item.qty).value,
          ),
        )
        ?.reduce((acc, current) => acc + current, 0);

      return summedDiscounts;
    } else {
      return 0;
    }
  });

  const totalProductDiscounts = productDiscounts?.reduce((acc, current) => acc + current, 0) ?? 0;

  let total = currency(totalProductDiscounts);

  if (shippingMethod) {
    const applicableDeliveryCodes = appliedCodes
      ?.filter(
        (a) =>
          a.type === 'delivery' &&
          a?.active &&
          (a?.services?.length === 0 || a?.services?.includes(shippingMethod ?? '')) &&
          cartTotal >= (a?.spendQualify ?? 0),
      )
      ?.map((code) => {
        return { discountAmount: calculateEffectiveAmount(code, shippingMethodAmount ?? 0), code };
      })
      ?.sort((a, b) => a.discountAmount <= b.discountAmount);

    if (applicableDeliveryCodes?.length > 0) {
      const selectedCode = applicableDeliveryCodes[0];

      usedCodes = [...usedCodes, selectedCode.code];

      total = total.add(selectedCode.discountAmount);
    }
  }

  const groupedUsedCodes = _.chain(usedCodes)
    .groupBy((a) => a?.code)
    .map((a) => a[0])
    .value();

  return { total, usedCodes: groupedUsedCodes };
};
